import { EM_DASH } from "constants/index";

import { useState } from "react";

import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { Typography } from "@portex-pro/ui-components";
import { ShipperAddress } from "api/rest/address/types";
import { Address } from "api/types/generated-types";
import { Shipment } from "app/pages/shipments/types/domain";
import formatAddress from "app/pages/shipments/utils/formatAddress";
import formatContact from "app/pages/shipments/utils/formatContact";
import formatEquipment from "app/pages/shipments/utils/formatEquipment";
import LocationPicker from "components/addresses/LocationPicker";
import DoubleTimeRangeSelector from "components/DoubleTimeRangeSelector";
import EditableColumnList, { EditableColumnRowProps } from "components/EditableColumnList";
import LoadTypeChip from "components/LoadTypeChip";
import SingleCalendarPicker from "components/SingleCalendarPicker";
import SingleTimeRangeSelector from "components/SingleTimeRangeSelector";
import Text from "components/Text";
import withAsync from "components/withAsync";
import ContainerCargoGroups from "features/cargo-groups/ContainerCargoGroups";
import { getCargoGroupEquipmentString } from "features/cargo-groups/utils/getCargoGroupEquipmentString";
import CargoGroupsOverviewView from "features/cargo-groups/views/CargoGroupsOverviewView";
import { FtlCargoGroupFormView } from "features/cargo-groups/views/FtlCargoGroupFormView";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { TimeRange } from "types/TimeRange";
import { formatUSD } from "utils/formatCurrency";
import { replaceInArray } from "utils/replaceInArray";
import AddressContacts from "views/addresses/AddressContacts";

import { useEditableQuoteRequest } from "../../hooks/quotes/useEditableQuoteRequest";
import QuoteHistoryView from "../../views/quotes/QuoteHistoryView";

const QuoteDetailsContainer = withAsync({
  useHook: useEditableQuoteRequest,
  Component: ({ loadedData: { quote, quoteRequest, isEditing, setEditing, setQuoteRequestByPath, save } }) => {
    const { t } = useTranslation(["shipper", "common", "shipments", "quoteDetails", "quoteRequest"]);
    const [cargoGroupExpanded, setCargoGroupExpanded] = useState<number | false>(0);

    const [useTimeRange, setUseTimeRange] = useState(
      quoteRequest.stops.map(
        (stop) =>
          !!stop.start &&
          !!stop.end &&
          stop.start !== stop.end &&
          !stop.is_time_tbd &&
          !DateTime.fromISO(stop.start, { zone: stop.address?.iana_timezone ?? DateTime.local().zone })
            .endOf("day")
            .equals(DateTime.fromISO(stop.end, { zone: stop.address?.iana_timezone ?? DateTime.local().zone }))
      )
    );

    const handleDateChange = (value: DateTime | null, index: number) => {
      const stop = quoteRequest.stops?.[index];
      if (!value) {
        setQuoteRequestByPath({
          path: `stops.${index}`,
          value: { ...quoteRequest.stops?.[index], start: null, end: null, is_time_tbd: false },
        });
        return;
      }
      let start = value
        ?.startOf("day")
        .setZone(stop?.address?.iana_timezone ?? DateTime.local().zone, { keepLocalTime: true });
      let end = value
        ?.endOf("day")
        .setZone(stop?.address?.iana_timezone ?? DateTime.local().zone, { keepLocalTime: true });

      if (start && stop?.start && stop.address?.iana_timezone) {
        start = start
          .setZone(stop.address.iana_timezone, { keepLocalTime: true })
          .set({ hour: DateTime.fromISO(stop.start).hour, minute: DateTime.fromISO(stop.start).minute });
      }
      if (end && stop?.end && stop.address?.iana_timezone) {
        end = end
          .setZone(stop.address.iana_timezone, { keepLocalTime: true })
          .set({ hour: DateTime.fromISO(stop.end).hour, minute: DateTime.fromISO(stop.end).minute });
      }
      setQuoteRequestByPath({
        path: `stops.${index}`,
        value: {
          ...quoteRequest.stops?.[index],
          start: start.toISO(),
          end: end.toISO(),
          is_time_tbd: quoteRequest.stops?.[0]?.is_time_tbd !== undefined ? undefined : true,
        },
      });
    };

    const handleTimeRangeChange = (range: TimeRange | null, index: number) => {
      const stop = quoteRequest.stops?.[index];
      if (!range) {
        setQuoteRequestByPath({
          path: `stops.${index}`,
          value: { ...quoteRequest.stops?.[index], start: null, end: null, is_time_tbd: false },
        });
        return;
      }
      let start: MaterialUiPickersDate | TimeRange | undefined = range.start;
      let end: MaterialUiPickersDate | TimeRange | undefined = range.end;

      if (stop?.address?.iana_timezone) {
        start = start?.setZone(stop?.address?.iana_timezone, { keepLocalTime: true });
        end = end?.setZone(stop?.address?.iana_timezone, { keepLocalTime: true });
      }
      setQuoteRequestByPath({
        path: `stops.${index}`,
        value: {
          ...quoteRequest.stops?.[index],
          start: start?.toISO(),
          end: end?.toISO(),
          is_time_tbd: range.isTimeTBD,
        },
      });
    };

    return (
      <div className="w-full pb-10 space-y-4">
        <EditableColumnList
          title={t("common:quoteDetails")}
          rows={[
            {
              label: t("common:totalAmount"),
              type: "custom",
              value: <>{formatUSD(quote.total_amount)}</>,
            },
            {
              label: t("common:bookingNotes"),
              type: "custom",
              value: quote.booking_notes ?? EM_DASH,
            },
            {
              label: t("common:notes"),
              type: "custom",
              value: quote.notes ?? EM_DASH,
            },
            {
              label: t("common:freightCharges"),
              labelTextProps: { typographyProps: { style: { textTransform: "uppercase" } } },
              type: "custom",
              value:
                quote.mode === "DRAYAGE"
                  ? formatUSD(quote.line_items.reduce((total, lineItem) => total + lineItem.amount, 0))
                  : EM_DASH,
              hide: quote.mode !== "DRAYAGE",
            },
            ...(quote.mode === "DRAYAGE"
              ? quote.line_items
                  .filter((lineItem) => lineItem.type === "FREIGHT_CHARGE" && !!lineItem.cargo_group)
                  .map(
                    (lineItem): EditableColumnRowProps => ({
                      label: lineItem.cargo_group ? getCargoGroupEquipmentString(lineItem.cargo_group, t) : EM_DASH,
                      labelTextProps: { typographyProps: { style: { textTransform: "uppercase" } } },
                      type: "custom",
                      value: formatUSD(lineItem.amount),
                    })
                  )
              : []),
            ...(quote.mode === "DRAYAGE"
              ? quote.line_items
                  .filter((lineItem) => lineItem.type === "ADDITIONAL_CHARGE")
                  .map(
                    (lineItem): EditableColumnRowProps => ({
                      label: t("common:additional"),
                      labelTextProps: { typographyProps: { style: { textTransform: "uppercase" } } },
                      type: "custom",
                      value: formatUSD(lineItem.amount),
                    })
                  )
              : []),
            {
              label: t("common:totalAmount"),
              type: "custom",
              value: formatUSD(quote.total_amount),
              hide: quote.mode !== "DRAYAGE",
            },
          ]}
        />
        <EditableColumnList
          title={t("common:requestDetails")}
          rows={[
            {
              label: t("common:mode"),
              type: "custom",
              value: <LoadTypeChip mode={quoteRequest.mode} />,
            },
            {
              label: t("shipper:equipmentType"),
              type: "custom",
              hide: quoteRequest.mode !== "DRAYAGE",
              value: (
                <>
                  {quoteRequest.cargo_groups.map((cargoGroup, i) => (
                    <span key={i}>
                      {getCargoGroupEquipmentString(cargoGroup, t)}
                      <br />
                    </span>
                  ))}
                </>
              ),
            },
            {
              label: t("shipper:equipmentType"),
              type: "custom",
              hide: quoteRequest.mode !== "FTL",
              value: formatEquipment(t, {
                loadSpec: {
                  trailerSize: quoteRequest.load_spec?.trailer_size,
                  trailerType: quoteRequest.load_spec?.trailer_type,
                  minTemp: quoteRequest.load_spec?.min_temp,
                  maxTemp: quoteRequest.load_spec?.max_temp,
                },
              } as Shipment),
            },
            {
              label: t("common:incoterms"),
              type: "custom",
              value: quoteRequest.incoterms ?? EM_DASH,
              hide: quoteRequest.mode !== "DRAYAGE",
            },
            {
              label: t("common:hazardousCommodities"),
              type: "custom",
              value: quoteRequest.is_hazardous ? t("common:yes") : t("common:no"),
            },
            {
              label: t("common:customs"),
              type: "custom",
              value: quoteRequest.include_customs_clearance ? t("common:required") : t("common:notRequired"),
              hide: quoteRequest.mode !== "DRAYAGE",
            },
            {
              label: t("common:insurance"),
              type: "custom",
              value: quoteRequest.insurance_required ? t("common:required") : t("common:notRequired"),
              hide: quoteRequest.mode !== "DRAYAGE",
            },
            {
              label: t("common:unloadingDetails"),
              type: "custom",
              value: quoteRequest.unloading_details
                ? t(`common:unloadingDetails_map.${quoteRequest.unloading_details}`)
                : EM_DASH,
            },
            {
              label: t("common:goodsValue"),
              type: "custom",
              value: quoteRequest.goods_value ? formatUSD(quoteRequest.goods_value) : EM_DASH,
            },
          ]}
        />

        {quoteRequest.mode === "DRAYAGE" && (
          <div className="border border-grey-400 rounded w-full bg-white mt-4">
            <div className="flex flex-row py-3 px-4 w-full border-b">
              <Text size="medium" weight="bold">
                {t("common:containers")}
              </Text>
            </div>
            {isEditing ? (
              <ContainerCargoGroups />
            ) : (
              <div className="w-full flex flex-row items-center py-3 px-4">
                <CargoGroupsOverviewView cargoGroups={quoteRequest.cargo_groups} variant={"container"} />
              </div>
            )}
          </div>
        )}

        {quoteRequest.mode === "FTL" && !!quoteRequest.cargo_groups.length && (
          <div className="border border-grey-400 rounded w-full bg-white mt-4">
            <div className="flex flex-row py-3 px-4 w-full border-b">
              <Text size="medium" weight="bold">
                {t("common:cargoDetails")}
              </Text>
            </div>
            {isEditing ? (
              <FtlCargoGroupFormView
                divProps={{ className: "bg-white" }}
                sectionProps={{ className: "mb-0" }}
                expanded={cargoGroupExpanded}
                onExpandChange={setCargoGroupExpanded}
              />
            ) : (
              <div className="w-full flex flex-row items-center py-3 px-4">
                <CargoGroupsOverviewView cargoGroups={quoteRequest.cargo_groups} variant={"handlingUnit"} />
              </div>
            )}
          </div>
        )}

        {quoteRequest.stops.map((stop, index) => {
          const contacts = stop.address?.address_contacts;

          return (
            <EditableColumnList
              title={
                index === 0
                  ? t("shipper:pickup")
                  : index === quoteRequest.stops.length - 1
                  ? t("shipper:delivery")
                  : t("common:stopLabel", { no: index })
              }
              isEditing={isEditing}
              setEditing={setEditing}
              onSave={() => save()}
              disableEditing={quoteRequest.status === "BOOKED" || quoteRequest.status === "CLOSED"}
              rows={[
                {
                  label: t("shipper:location"),
                  value: stop.address
                    ? formatAddress.fromSnakeCase(
                        Object.entries(quoteRequest.stops[index].address ?? {}).reduce((add, [key, value]) => {
                          // @ts-expect-error same as above
                          add[key] = value ?? null;
                          return add;
                        }, {}),
                        "long"
                      )
                    : EM_DASH,
                  type: "custom",
                  customEditingComponent: (
                    <LocationPicker
                      value={(stop.address ?? {}) as unknown as Partial<Address>}
                      mode={quoteRequest.mode}
                      toDoor={quoteRequest.mode === "FCL" || quoteRequest.mode === "AIR"}
                      variant="default"
                      startIconPallete="grey"
                      onChange={(value) => setQuoteRequestByPath({ path: `stops.${index}.address`, value })}
                    />
                  ),
                },
                {
                  label: t("common:addresses.manualName"),
                  value: quoteRequest.stops[index].address?.name ?? EM_DASH,
                  type: "string",
                  placeholder: EM_DASH,
                  editableProps: {
                    onChange: (e) =>
                      setQuoteRequestByPath({ path: `stops.${index}.address.name`, value: e.target.value }),
                    style: { width: "100%" },
                  },
                },
                {
                  label: t("shipments:shipmentDetails_dateAndTime_label"),
                  type: "custom",
                  value: stop.start ? (
                    <>
                      {DateTime.fromISO(stop.start, {
                        zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                      }).toLocaleString(DateTime.DATETIME_FULL)}
                      {!!stop.end && stop.end !== stop.start && (
                        <span>
                          {" - "}
                          {DateTime.fromISO(stop.end, {
                            zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                          }).toFormat("h:mm a")}
                        </span>
                      )}
                    </>
                  ) : (
                    EM_DASH
                  ),
                  customEditingComponent: (
                    <div className="flex flex-col space-y-3">
                      <SingleCalendarPicker
                        value={
                          stop.start
                            ? DateTime.fromISO(stop.start, {
                                zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                              })
                            : null
                        }
                        startIconPallete="grey"
                        onChange={(date) => handleDateChange(date, index)}
                      />
                      {useTimeRange[index] ? (
                        <DoubleTimeRangeSelector
                          onChange={(value) => handleTimeRangeChange(value, index)}
                          value={{
                            start: stop.start
                              ? DateTime.fromISO(stop.start, {
                                  zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                                })
                              : null,
                            end: stop.end
                              ? DateTime.fromISO(stop.end, {
                                  zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                                })
                              : null,
                            isTimeTBD: stop.is_time_tbd,
                          }}
                          style={{ flex: 1 }}
                          disabled={!quoteRequest.stops?.[index].start}
                        />
                      ) : (
                        <SingleTimeRangeSelector
                          onChange={(value) => handleTimeRangeChange(value, index)}
                          disabled={!quoteRequest.stops?.[index].start}
                          key={index}
                          value={{
                            start: stop.start
                              ? DateTime.fromISO(stop.start, {
                                  zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                                })
                              : null,
                            end: stop.end
                              ? DateTime.fromISO(stop.end, {
                                  zone: stop.address?.iana_timezone ?? DateTime.local().zone,
                                })
                              : null,
                            isTimeTBD: stop.is_time_tbd,
                          }}
                          style={{ flex: 1 }}
                          startIconPallete="grey"
                        />
                      )}
                      <div className="z-10">
                        {useTimeRange[index] ? (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginLeft: "auto", cursor: "pointer", textDecoration: "underline" }}
                            onClick={() => setUseTimeRange(replaceInArray(useTimeRange, index, false))}
                          >
                            {t("quoteRequest:LocationStep.useTime")}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginLeft: "auto", cursor: "pointer", textDecoration: "underline" }}
                            onClick={() => setUseTimeRange(replaceInArray(useTimeRange, index, true))}
                          >
                            {t("quoteRequest:LocationStep.useTimeRange")}
                          </Typography>
                        )}
                      </div>
                    </div>
                  ),
                },
                {
                  label: t("shipper:operationsContact"),
                  type: "custom",
                  value: contacts
                    ? contacts
                        .map((contact) =>
                          formatContact({
                            email: contact.email ?? "",
                            firstName: contact.first_name ?? "",
                            lastName: contact.last_name ?? "",
                            phoneNumber: contact.phone_number ?? "",
                          })
                        )
                        .join("\n")
                    : EM_DASH,
                  customEditingComponent: (
                    <AddressContacts
                      address={(stop.address ?? {}) as unknown as ShipperAddress}
                      isSubmitting={false}
                      onChange={(address) => setQuoteRequestByPath({ path: `stops.${index}.address`, value: address })}
                      showLocationName={false}
                    />
                  ),
                },
                {
                  label: t("common:notes"),
                  type: "string",
                  value: stop.note,
                  placeholder: EM_DASH,
                  editableProps: {
                    onChange: (e) => setQuoteRequestByPath({ path: `stops.${index}.note`, value: e.target.value }),
                    style: { width: "100%" },
                    multiline: true,
                    rowsMax: 4,
                    rows: 3,
                  },
                },
                {
                  label: t("common:bookingNotes"),
                  type: "string",
                  value: stop.booking_notes,
                  placeholder: EM_DASH,
                  editableProps: {
                    onChange: (e) =>
                      setQuoteRequestByPath({ path: `stops.${index}.booking_notes`, value: e.target.value }),
                    style: { width: "100%" },
                    multiline: true,
                    rowsMax: 4,
                    rows: 3,
                  },
                },
                {
                  label: t("common:referenceNo"),
                  type: "string",
                  value: stop.reference_number,
                  placeholder: EM_DASH,
                  editableProps: {
                    onChange: (e) =>
                      setQuoteRequestByPath({ path: `stops.${index}.reference_number`, value: e.target.value }),
                    style: { width: "100%" },
                  },
                },
              ]}
            />
          );
        })}

        <QuoteHistoryView quote={quote} historicalQuotes={quoteRequest.historical_quotes} />
      </div>
    );
  },
});

export default QuoteDetailsContainer;
